import { createApi, fetchBaseQuery, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import {
  AssignTaskDto,
  CancelTaskDto,
  CloseTaskDto,
  DismissTaskDto,
  CreateTaskDto,
  HoldTaskDto,
  ListTasksParams,
  OverrideTaskPriorityDto,
  ResumeTaskDto,
  TaskAllowedActionsResponseDto,
  TaskEventResponseDto,
  TaskListResponseDto,
  TaskOutcomeResponseDto,
  TaskRelationshipResponseDto,
  TaskResponseDto,
  TaskTypeResponseDto,
  TaskWorkType,
  UnassignTaskDto,
  UpdateTaskMetadataDto,
} from '@/types/task';
import { BASE_URL } from '@/utils/constants/apiEndpoints';

/** GET /tasks rejects `page`. Pagination is `skip` and `limit`. */
function taskListParams(arg: ListTasksParams | void): ListTasksParams {
  const params = { ...(arg ?? {}) } as ListTasksParams & { page?: number };
  if (params.page != null && params.skip == null) {
    const limit = params.limit ?? 25;
    params.skip = Math.max(0, (Number(params.page) - 1) * limit);
  }
  delete params.page;
  return params;
}

export type {
  AssignTaskDto,
  CancelTaskDto,
  CloseTaskDto,
  CreateTaskDto,
  HoldTaskDto,
  ListTasksParams,
  OrganizationResponseDto,
  OverrideTaskPriorityDto,
  ResumeTaskDto,
  UnassignTaskDto,
  TaskAdminPriority,
  TaskAlertType,
  TaskAllowedActionsResponseDto,
  TaskAppointmentType,
  TaskBusinessState,
  TaskChannel,
  TaskDetailsDto,
  TaskEventChangeResponseDto,
  TaskEventResponseDto,
  TaskEventType,
  TaskListResponseDto,
  TaskOutcomeResponseDto,
  TaskPriorityBand,
  TaskResponseDto,
  TaskRoutingState,
  TaskTypeResponseDto,
  TaskWorkType,
  UpdateTaskMetadataDto,
  UserResponseDto,
} from '@/types/task';

export const tasksApiSlice = createApi({
  reducerPath: 'tasksApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set('authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ['Tasks', 'TaskOutcomes', 'TaskTypes'],
  endpoints: (builder) => ({
    listTasks: builder.query<TaskListResponseDto, ListTasksParams | void>({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const params = taskListParams(arg);
        const alertTypes = params.alertTypes?.length
          ? params.alertTypes
          : params.alertType
            ? [params.alertType]
            : [];
        const appointmentTypes = params.appointmentTypes?.length
          ? params.appointmentTypes
          : params.appointmentType
            ? [params.appointmentType]
            : [];
        const single = alertTypes.length <= 1 && appointmentTypes.length <= 1;
        const requestParams = (alertType?: string, appointmentType?: string) => {
          const {
            alertTypes: _alertTypes,
            appointmentTypes: _appointmentTypes,
            alertType: _alertType,
            appointmentType: _appointmentType,
            noneAlertTaskTypeIds,
            taskTypeIds,
            ...rest
          } = params;
          let ids = taskTypeIds;
          if (alertType === 'none' && noneAlertTaskTypeIds) {
            const allowed = new Set(noneAlertTaskTypeIds);
            const base = taskTypeIds?.length ? taskTypeIds : noneAlertTaskTypeIds;
            ids = base.filter((id) => allowed.has(id));
            if (ids.length === 0) return null;
          }
          return {
            ...rest,
            ...(ids?.length ? { taskTypeIds: ids } : {}),
            ...(alertType ? { alertType } : {}),
            ...(appointmentType ? { appointmentType } : {}),
          };
        };
        if (single) {
          const singleParams = requestParams(alertTypes[0], appointmentTypes[0]);
          if (!singleParams) return { data: { items: [], total: 0 } };
          const result = await baseQuery({
            url: '/tasks',
            params: singleParams,
          });
          if (result.error) return { error: result.error as FetchBaseQueryError };
          return { data: result.data as TaskListResponseDto };
        }

        const alerts = alertTypes.length ? alertTypes : [undefined];
        const appointments = appointmentTypes.length ? appointmentTypes : [undefined];
        const seen = new Set<string>();
        const collected: TaskListResponseDto['items'] = [];
        for (const alertType of alerts) {
          for (const appointmentType of appointments) {
            let skip = 0;
            let expected = Number.POSITIVE_INFINITY;
            const comboParams = requestParams(alertType, appointmentType);
            if (!comboParams) continue;
            while (skip < 300) {
              const result = await baseQuery({
                url: '/tasks',
                params: {
                  ...comboParams,
                  limit: 100,
                  skip,
                },
              });
              if (result.error) return { error: result.error as FetchBaseQueryError };
              const data = result.data as TaskListResponseDto;
              const batch = data.items ?? [];
              expected = data.total ?? batch.length;
              for (const item of batch) {
                if (seen.has(item.id)) continue;
                seen.add(item.id);
                collected.push(item);
              }
              skip += batch.length;
              if (batch.length < 100 || skip >= expected) break;
            }
          }
        }
        const direction = params.sortDirection === 'ASC' ? 1 : -1;
        collected.sort((a, b) => {
          const value = (task: TaskListResponseDto['items'][number]) => {
            switch (params.sortBy) {
              case 'subject': return task.subject.toLowerCase();
              case 'taskType': return task.taskType?.name?.toLowerCase() ?? '';
              case 'organizationName': return task.organization?.name?.toLowerCase() ?? '';
              case 'assigneeName':
                return (task.assignee?.name || `${task.assignee?.given ?? ''} ${task.assignee?.family ?? ''}`).trim().toLowerCase();
              case 'routingPriority': return task.priorityRank ?? Number.MAX_SAFE_INTEGER;
              case 'businessStatus': return task.businessState;
              default: return task.createdAt;
            }
          };
          const left = value(a);
          const right = value(b);
          if (left < right) return -direction;
          if (left > right) return direction;
          return 0;
        });
        const skip = params.skip ?? 0;
        const limit = params.limit ?? collected.length;
        return { data: { items: collected.slice(skip, skip + limit), total: collected.length } };
      },
      providesTags: ['Tasks'],
    }),
    getTask: builder.query<TaskResponseDto, string>({
      query: (id) => `/tasks/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Tasks', id }],
    }),
    getTaskAllowedActions: builder.query<TaskAllowedActionsResponseDto, string>({
      query: (id) => `/tasks/${id}/allowed-actions`,
      providesTags: (_r, _e, id) => [{ type: 'Tasks', id }],
    }),
    getTaskRelationships: builder.query<TaskRelationshipResponseDto[], string>({
      query: (id) => `/tasks/${id}/relationships`,
      transformResponse: (res: TaskRelationshipResponseDto[] | { items: TaskRelationshipResponseDto[] }) =>
        Array.isArray(res) ? res : (res.items ?? []),
    }),
    getTaskEvents: builder.query<TaskEventResponseDto[], string>({
      query: (id) => `/tasks/${id}/events`,
      transformResponse: (res: TaskEventResponseDto[] | { items: TaskEventResponseDto[] }) =>
        Array.isArray(res) ? res : (res.items ?? []),
    }),
    listTaskOutcomes: builder.query<
      TaskOutcomeResponseDto[],
      { workType?: TaskWorkType; businessState?: 'completed' | 'canceled' | 'on_hold'; isActive?: boolean } | void
    >({
      query: (params) => ({ url: '/tasks/outcomes', params: params ?? {} }),
      transformResponse: (res: TaskOutcomeResponseDto[] | { items: TaskOutcomeResponseDto[] }) =>
        Array.isArray(res) ? res : (res.items ?? []),
      providesTags: ['TaskOutcomes'],
    }),
    listTaskTypes: builder.query<TaskTypeResponseDto[], { workType?: TaskWorkType } | void>({
      query: (params) => ({ url: '/tasks/types', params: params ?? {} }),
      transformResponse: (res: TaskTypeResponseDto[] | { items: TaskTypeResponseDto[] }) =>
        Array.isArray(res) ? res : res.items,
      providesTags: ['TaskTypes'],
    }),
    cancelTask: builder.mutation<TaskResponseDto, { id: string; body: CancelTaskDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/cancel`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    closeTask: builder.mutation<TaskResponseDto, { id: string; body: CloseTaskDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/close`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    dismissTask: builder.mutation<TaskResponseDto, { id: string; body: DismissTaskDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/dismiss`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    holdTask: builder.mutation<TaskResponseDto, { id: string; body: HoldTaskDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/hold`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    resumeTask: builder.mutation<TaskResponseDto, { id: string; body: ResumeTaskDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/resume`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    updateTaskMetadata: builder.mutation<TaskResponseDto, { id: string; body: UpdateTaskMetadataDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    createTask: builder.mutation<TaskResponseDto, CreateTaskDto>({
      query: (body) => ({ url: '/tasks', method: 'POST', body }),
      invalidatesTags: ['Tasks'],
    }),
    overrideTaskPriority: builder.mutation<TaskResponseDto, { id: string; body: OverrideTaskPriorityDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/priority-override`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    patchTask: builder.mutation<TaskResponseDto, { id: string } & Record<string, unknown>>({
      query: ({ id, ...body }) => ({ url: `/tasks/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    assignTask: builder.mutation<TaskResponseDto, { id: string; body: AssignTaskDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/assign`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
    unassignTask: builder.mutation<TaskResponseDto, { id: string; body: UnassignTaskDto }>({
      query: ({ id, body }) => ({ url: `/tasks/${id}/unassign`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => ['Tasks', { type: 'Tasks', id }],
    }),
  }),
});

export const {
  useCreateTaskMutation,
  useListTasksQuery,
  useLazyListTasksQuery,
  useGetTaskQuery,
  useLazyGetTaskQuery,
  useGetTaskAllowedActionsQuery,
  useGetTaskEventsQuery,
  useGetTaskRelationshipsQuery,
  useListTaskOutcomesQuery,
  useListTaskTypesQuery,
  useCancelTaskMutation,
  useCloseTaskMutation,
  useDismissTaskMutation,
  useHoldTaskMutation,
  useResumeTaskMutation,
  useUpdateTaskMetadataMutation,
  useOverrideTaskPriorityMutation,
  usePatchTaskMutation,
  useAssignTaskMutation,
  useUnassignTaskMutation,
} = tasksApiSlice;
